import React, { ReactNode } from 'react';
import { useIsElectronContext, useElectronVersion, useIsLegacyElectron } from '../../context/ElectronContext';
import { satisfiesVersion } from '../../helpers/semver';

interface ElectronGateProps {
  /** Contenido a renderizar solo cuando está en Electron */
  children: ReactNode;
  /** Contenido alternativo para modo web o versión no compatible (opcional) */
  fallback?: ReactNode;
  /** Si true, invierte la lógica (muestra children solo en web) */
  webOnly?: boolean;
  /** Si true, solo muestra children cuando la API NO es legacy (requiere sippar-electron modular) */
  modernOnly?: boolean;
  /** Si true, solo muestra children cuando la API ES legacy (desktop-app antiguo) */
  legacyOnly?: boolean;
  /** Versión mínima requerida (inclusive) */
  minVersion?: string;
  /** Versión máxima permitida (inclusive) */
  maxVersion?: string;
  /** Versión exacta requerida */
  exactVersion?: string;
  /** Si true, espera a que la versión esté disponible antes de renderizar */
  waitForVersion?: boolean;
  /** Contenido a mostrar mientras se carga la versión (solo si waitForVersion=true) */
  loading?: ReactNode;
}

/**
 * Componente que renderiza condicionalmente basado en el entorno Electron/Web
 * y opcionalmente basado en la versión de la app Electron
 *
 * @example
 * // Solo en Electron
 * <ElectronGate>
 *   <ScreenshotButton />
 * </ElectronGate>
 *
 * @example
 * // En Electron con fallback para web
 * <ElectronGate fallback={<WebStorageNotice />}>
 *   <LocalStorageFeature />
 * </ElectronGate>
 *
 * @example
 * // Solo en web (invirtiendo la lógica)
 * <ElectronGate webOnly>
 *   <DownloadAppBanner />
 * </ElectronGate>
 *
 * @example
 * // Solo en Electron 2.0.0 o superior
 * <ElectronGate minVersion="2.0.0" fallback={<UpdateRequired />}>
 *   <NewFeature />
 * </ElectronGate>
 *
 * @example
 * // Solo entre versiones específicas
 * <ElectronGate minVersion="1.5.0" maxVersion="2.0.0">
 *   <LegacyComponent />
 * </ElectronGate>
 *
 * @example
 * // Solo en Electron moderno (no legacy desktop-app)
 * <ElectronGate modernOnly fallback={<UpdateAppBanner />}>
 *   <OfflineStorageFeature />
 * </ElectronGate>
 *
 * @example
 * // Solo en desktop-app legacy
 * <ElectronGate legacyOnly>
 *   <LegacyFeatureNotice />
 * </ElectronGate>
 */
export function ElectronGate({
  children,
  fallback = null,
  webOnly = false,
  modernOnly = false,
  legacyOnly = false,
  minVersion,
  maxVersion,
  exactVersion,
  waitForVersion = false,
  loading = null,
}: ElectronGateProps) {
  const isElectron = useIsElectronContext();
  const isLegacy = useIsLegacyElectron();
  const { version, loading: versionLoading } = useElectronVersion();

  // Determinar si hay restricciones de versión
  const hasVersionConstraint = !!(minVersion || maxVersion || exactVersion);

  // Si webOnly está activo, ignoramos restricciones de versión
  if (webOnly) {
    return <>{!isElectron ? children : fallback}</>;
  }

  // Si no estamos en Electron, mostrar fallback
  if (!isElectron) {
    return <>{fallback}</>;
  }

  // Filtro por tipo de API (legacy vs moderno)
  if (modernOnly && isLegacy) {
    return <>{fallback}</>;
  }

  if (legacyOnly && !isLegacy) {
    return <>{fallback}</>;
  }

  // Si hay restricciones de versión
  if (hasVersionConstraint) {
    // Si está cargando la versión y queremos esperar
    if (versionLoading && waitForVersion) {
      return <>{loading}</>;
    }

    // Verificar si la versión cumple las restricciones
    const versionSatisfied = satisfiesVersion(version, {
      min: minVersion,
      max: maxVersion,
      exact: exactVersion,
    });

    if (!versionSatisfied) {
      return <>{fallback}</>;
    }
  }

  // Todas las condiciones cumplidas
  return <>{children}</>;
}

/**
 * HOC para envolver componentes que solo deben renderizarse en Electron
 * con soporte opcional de versiones
 *
 * @example
 * const ElectronOnlyButton = withElectronOnly(ScreenshotButton);
 * const NewFeatureButton = withElectronOnly(NewFeature, OldFeature, { minVersion: '2.0.0' });
 */
export function withElectronOnly<P extends object>(
  Component: React.ComponentType<P>,
  Fallback?: React.ComponentType<P>,
  versionConstraint?: { minVersion?: string; maxVersion?: string; exactVersion?: string }
) {
  return function ElectronOnlyWrapper(props: P) {
    const isElectron = useIsElectronContext();
    const { version } = useElectronVersion();

    if (!isElectron) {
      return Fallback ? <Fallback {...props} /> : null;
    }

    // Si hay restricciones de versión, verificarlas
    if (versionConstraint) {
      const versionSatisfied = satisfiesVersion(version, {
        min: versionConstraint.minVersion,
        max: versionConstraint.maxVersion,
        exact: versionConstraint.exactVersion,
      });

      if (!versionSatisfied) {
        return Fallback ? <Fallback {...props} /> : null;
      }
    }

    return <Component {...props} />;
  };
}

/**
 * HOC para envolver componentes que solo deben renderizarse en Web
 */
export function withWebOnly<P extends object>(
  Component: React.ComponentType<P>,
  Fallback?: React.ComponentType<P>
) {
  return function WebOnlyWrapper(props: P) {
    const isElectron = useIsElectronContext();

    if (isElectron) {
      return Fallback ? <Fallback {...props} /> : null;
    }

    return <Component {...props} />;
  };
}
